import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, BookOpen, Save, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function MutualStrategies() {
  const navigate = useNavigate();
  
  const [need, setNeed] = useState('');
  const [personA, setPersonA] = useState('');
  const [personB, setPersonB] = useState('');
  const [strategiesA, setStrategiesA] = useState(['']);
  const [strategiesB, setStrategiesB] = useState(['']);
  const [mutualStrategies, setMutualStrategies] = useState(['']);

  useEffect(() => {
    const saved = localStorage.getItem('mutualStrategiesWorksheet');
    if (saved) {
      const data = JSON.parse(saved);
      setNeed(data.need || '');
      setPersonA(data.personA || '');
      setPersonB(data.personB || '');
      setStrategiesA(data.strategiesA || ['']);
      setStrategiesB(data.strategiesB || ['']);
      setMutualStrategies(data.mutualStrategies || ['']);
    }
  }, []);

  const saveProgress = () => {
    localStorage.setItem('mutualStrategiesWorksheet', JSON.stringify({
      need, personA, personB, strategiesA, strategiesB, mutualStrategies
    }));
    toast.success('Worksheet saved!');
  };

  const reset = () => {
    if (window.confirm('Reset worksheet?')) {
      setNeed('');
      setPersonA('');
      setPersonB('');
      setStrategiesA(['']);
      setStrategiesB(['']);
      setMutualStrategies(['']);
      localStorage.removeItem('mutualStrategiesWorksheet');
      toast.success('Reset complete!');
    }
  };

  const addStrategy = (type) => {
    if (type === 'A') setStrategiesA([...strategiesA, '']);
    else if (type === 'B') setStrategiesB([...strategiesB, '']);
    else setMutualStrategies([...mutualStrategies, '']);
  };

  const updateStrategy = (type, idx, value) => {
    if (type === 'A') {
      const updated = [...strategiesA];
      updated[idx] = value;
      setStrategiesA(updated);
    } else if (type === 'B') {
      const updated = [...strategiesB];
      updated[idx] = value;
      setStrategiesB(updated);
    } else {
      const updated = [...mutualStrategies];
      updated[idx] = value;
      setMutualStrategies(updated);
    }
  };

  const deleteStrategy = (type, idx) => {
    if (type === 'A' && strategiesA.length > 1) setStrategiesA(strategiesA.filter((_, i) => i !== idx));
    else if (type === 'B' && strategiesB.length > 1) setStrategiesB(strategiesB.filter((_, i) => i !== idx));
    else if (mutualStrategies.length > 1) setMutualStrategies(mutualStrategies.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    const interval = setInterval(saveProgress, 30000);
    return () => clearInterval(interval);
  }, [need, personA, personB, strategiesA, strategiesB, mutualStrategies]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Worksheet for Finding Mutual Strategies</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={reset} variant="outline" size="sm" className="border-red-600 hover:bg-red-900/20 text-white">
                <RotateCcw className="h-4 w-4 mr-2" />Reset
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 26</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">For More Satisfying Relationships</h2>
            <p className="text-blue-100 leading-relaxed mb-3">
              In NVC, it is commonly understood that <span className="font-semibold">conflict is never between needs, but rather always between strategies</span>. 
              This worksheet helps two people discover mutual strategies for getting needs met.
            </p>
            <p className="text-blue-200 text-sm italic">
              We are much more likely to get our needs met when we focus on what we want, instead of what we don't want.
            </p>
          </div>

          {/* Step Instructions */}
          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4">Steps:</h3>
            <div className="space-y-2 text-slate-200">
              <p><span className="font-semibold text-white">1.</span> Choose a need you'd like to have met better and write it below</p>
              <p><span className="font-semibold text-white">2.</span> Each person writes their suggested strategies in their section</p>
              <p><span className="font-semibold text-white">3.</span> Discuss all strategies and find mutually satisfying ones to try</p>
            </div>
          </div>

          {/* Need Input */}
          <div className="mb-8">
            <label className="text-green-300 font-semibold text-lg mb-3 block">NEED(S):</label>
            <Input value={need} onChange={(e) => setNeed(e.target.value)} placeholder="e.g., connection, understanding, harmony..." className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 text-lg py-6" />
          </div>

          {/* Three Columns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Person A */}
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6">
              <Input value={personA} onChange={(e) => setPersonA(e.target.value)} placeholder="Person A name" className="bg-blue-900/30 border-blue-700/40 text-white placeholder:text-blue-300/40 mb-4 font-semibold" />
              <h4 className="text-blue-200 font-semibold mb-3">Person A's Strategies</h4>
              <div className="space-y-2">
                {strategiesA.map((s, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Textarea value={s} onChange={(e) => updateStrategy('A', idx, e.target.value)} placeholder={`Strategy ${idx + 1}`} className="bg-blue-900/30 border-blue-700/40 text-white placeholder:text-blue-300/40 min-h-[60px]" />
                    {strategiesA.length > 1 && <Button onClick={() => deleteStrategy('A', idx)} variant="outline" size="sm" className="border-red-600 text-white"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button onClick={() => addStrategy('A')} variant="outline" size="sm" className="w-full border-blue-600 text-blue-200 hover:bg-blue-900/20">
                  <Plus className="h-4 w-4 mr-2" />Add Strategy
                </Button>
              </div>
            </div>

            {/* Mutual Strategies */}
            <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6">
              <h4 className="text-green-200 font-semibold mb-3 text-center">✅ Mutual Strategies We Want to Try</h4>
              <div className="space-y-2">
                {mutualStrategies.map((s, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Textarea value={s} onChange={(e) => updateStrategy('M', idx, e.target.value)} placeholder={`Mutual strategy ${idx + 1}`} className="bg-green-900/30 border-green-700/40 text-white placeholder:text-green-300/40 min-h-[60px]" />
                    {mutualStrategies.length > 1 && <Button onClick={() => deleteStrategy('M', idx)} variant="outline" size="sm" className="border-red-600 text-white"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button onClick={() => addStrategy('M')} variant="outline" size="sm" className="w-full border-green-600 text-green-200 hover:bg-green-900/20">
                  <Plus className="h-4 w-4 mr-2" />Add Strategy
                </Button>
              </div>
            </div>

            {/* Person B */}
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6">
              <Input value={personB} onChange={(e) => setPersonB(e.target.value)} placeholder="Person B name" className="bg-purple-900/30 border-purple-700/40 text-white placeholder:text-purple-300/40 mb-4 font-semibold" />
              <h4 className="text-purple-200 font-semibold mb-3">Person B's Strategies</h4>
              <div className="space-y-2">
                {strategiesB.map((s, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Textarea value={s} onChange={(e) => updateStrategy('B', idx, e.target.value)} placeholder={`Strategy ${idx + 1}`} className="bg-purple-900/30 border-purple-700/40 text-white placeholder:text-purple-300/40 min-h-[60px]" />
                    {strategiesB.length > 1 && <Button onClick={() => deleteStrategy('B', idx)} variant="outline" size="sm" className="border-red-600 text-white"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                ))}
                <Button onClick={() => addStrategy('B')} variant="outline" size="sm" className="w-full border-purple-600 text-purple-200 hover:bg-purple-900/20">
                  <Plus className="h-4 w-4 mr-2" />Add Strategy
                </Button>
              </div>
            </div>
          </div>

          <p className="text-center text-slate-400 text-sm mt-6">💾 Auto-saves every 30 seconds</p>
        </div>
      </div>
    </div>
  );
}
